import styled, { css, keyframes, type DefaultTheme } from 'styled-components';
import {
  NODE_CARD_ACCENTS,
  NODE_CARD_DIMMED_OPACITY,
  NODE_CARD_EDIT_MODE_EDIT,
  NODE_CARD_EMPHASIS_DIMMED,
  NODE_CARD_EMPHASIS_HIGHLIGHTED,
  NODE_CARD_STATE_COMPLETED,
  NODE_CARD_STATE_DEFAULT,
  NODE_CARD_STATE_FAILED,
  NODE_CARD_STATE_INVALID,
  NODE_CARD_STATE_ORPHAN,
  NODE_CARD_STATE_RUNDONE,
  NODE_CARD_STATE_RUNNING,
  NODE_CARD_STATE_SELECTED,
  NODE_CARD_STATE_SOURCE,
  NODE_CARD_STATE_TERMINAL,
  NODE_CARD_VARIANT_OUTPUT,
  type NodeCardEditMode,
  type NodeCardEmphasis,
  type NodeCardState,
  type NodeCardVariant,
} from './NodeCardShell.constants';

// Ring mixes use `primaryBold`, not `primary` — our M3 `primary` is
// the design's `--secondary` and blends off-spec with `outline-variant`.
const primaryBoldFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryBold ?? '#8E8DFF';

const outlineVariantFor = (theme: DefaultTheme): string =>
  theme.colors?.outlineVariant ?? '#25263A';

const surfaceContainerLowestFor = (theme: DefaultTheme): string =>
  theme.colors?.surfaceContainerLowest ?? '#07080F';

const runErrorFor = (theme: DefaultTheme): string =>
  theme.colors?.runError ?? '#FF6B6B';

const orphanBorderFor = (theme: DefaultTheme): string =>
  theme.colors?.orphanBorder ?? '#E6C76A';

const successFixedFor = (theme: DefaultTheme): string =>
  theme.colors?.successFixed ?? '#4ADE80';

const nodeBodyInputBgFor = (theme: DefaultTheme): string =>
  theme.colors?.nodeBodyInputBg ?? '#0F0F17';

const accentStrongFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryEmphasisStrong ?? '#9892EE';

const accentFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryEmphasis ?? '#CFC9F5';

const isLightTheme = (theme: DefaultTheme): boolean => !!theme.isLightTheme;

const BASE_SHADOW = `
  0 1px 0 rgb(255 255 255 / 0.04) inset,
  0 0 0 1px rgb(0 0 0 / 0.20),
  0 2px 6px rgba(0, 0, 0, 0.22),
  0 12px 30px rgba(0, 0, 0, 0.28)
`;

const hoverShadow = (theme: DefaultTheme) => {
  const primary = primaryBoldFor(theme);
  return `
    0 1px 0 rgb(255 255 255 / 0.05) inset,
    0 0 0 1px color-mix(in oklch, ${primary} 25%, transparent),
    0 12px 28px rgba(0, 0, 0, 0.32)
  `;
};

interface RootProps {
  $state: NodeCardState;
  $variant: NodeCardVariant;
  $width: number;
  $editMode: NodeCardEditMode | null;
  $published: boolean;
  $dropTarget: boolean;
  $hovered: boolean;
  $emphasis: NodeCardEmphasis | null;
}

interface IconTileProps {
  $variant: NodeCardVariant;
}

interface CollapseProps {
  $collapsed: boolean;
}

const pulseGate = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgb(255 223 158 / 0.2); }
  50% { box-shadow: 0 0 0 4px rgb(255 223 158 / 0.3); }
`;

// Projected as CSS vars so child slots can inherit the accent
// without re-declaring per-variant rules.
const accentVars = ($variant: NodeCardVariant) => {
  const triplet = NODE_CARD_ACCENTS[$variant] ?? NODE_CARD_ACCENTS.neutral;
  return css`
    --node-card-accent: ${triplet.accent};
    --node-card-accent-strong: ${triplet.strong};
    --node-card-accent-ink: ${triplet.ink};
  `;
};

const baseBackground = (theme: DefaultTheme) => {
  if (isLightTheme(theme)) {
    return surfaceContainerLowestFor(theme);
  }
  const colors = theme.colors ?? {};
  const high = colors.surfaceContainer ?? '#181927';
  const low = colors.surfaceContainerLow ?? '#13141E';
  return `linear-gradient(180deg,
    color-mix(in oklch, ${high} 55%, ${low}) 0%,
    ${low} 38%)`;
};

// Primary wash at the top so the card reads as "being authored".
const editBackground = (theme: DefaultTheme) => {
  const primary = primaryBoldFor(theme);
  if (isLightTheme(theme)) {
    const white = surfaceContainerLowestFor(theme);
    return `linear-gradient(180deg,
      color-mix(in oklch, ${primary} 4%, ${white}),
      ${white} 120px), ${white}`;
  }
  const low = theme.colors?.surfaceContainerLow ?? '#13141E';
  return `linear-gradient(180deg,
    color-mix(in oklch, ${primary} 6%, ${low}),
    ${low} 120px), ${low}`;
};

// The resting-ring shadow string lives in its own helper so the
// source/terminal halos can LAYER on top of it (`<resting>, <halo>`)
// instead of overwriting it — overwriting drops the perimeter stroke
// in light mode and the base depth shadow in dark mode.
const restingShadow = (theme: DefaultTheme): string => {
  if (isLightTheme(theme)) {
    return `
      0 0 0 1px ${outlineVariantFor(theme)},
      0 1px 2px 0 rgba(20, 20, 50, 0.04)
    `;
  }
  return BASE_SHADOW;
};

const restingRing = ({
  theme,
  $published,
}: {
  theme: DefaultTheme;
  $published: boolean;
}) => {
  if (isLightTheme(theme)) {
    return css`
      outline: 0;
      box-shadow: ${restingShadow(theme)};
    `;
  }
  const stroke = `color-mix(in oklch, ${primaryBoldFor(theme)} 50%, ${outlineVariantFor(theme)})`;
  const width = $published ? '2px' : '1.5px';
  const style = $published ? 'solid' : 'dashed';
  return css`
    outline: ${width} ${style} ${stroke};
    outline-offset: 0;
    box-shadow: ${restingShadow(theme)};
  `;
};

// Source/terminal halos layer the design's "first/last node in the chain"
// whisper on top of the resting outline. They never appear alongside a
// run/error/selected state because the state machine routes those cases
// to their own ring helpers, mirroring the design's L12934 clear rules.
// The halo is appended to the resting shadow so the perimeter stroke
// (light) and base depth (dark) survive.
const sourceHalo = (props: { theme: DefaultTheme; $published: boolean }) => {
  const primary = primaryBoldFor(props.theme);
  const halo = `
    0 0 0 1px color-mix(in oklch, ${primary} 28%, transparent),
    0 8px 24px color-mix(in oklch, ${primary} 10%, transparent)
  `;
  return css`
    ${restingRing(props)}
    box-shadow: ${restingShadow(props.theme)}, ${halo};
  `;
};

const terminalHalo = (props: { theme: DefaultTheme; $published: boolean }) => {
  const green = successFixedFor(props.theme);
  const halo = `
    0 0 0 1px color-mix(in oklch, ${green} 30%, transparent),
    0 8px 24px color-mix(in oklch, ${green} 10%, transparent)
  `;
  return css`
    ${restingRing(props)}
    box-shadow: ${restingShadow(props.theme)}, ${halo};
  `;
};

const hoveredRing = ({ theme }: { theme: DefaultTheme }) => {
  if (isLightTheme(theme)) {
    const stroke = `color-mix(in oklch, ${accentStrongFor(theme)} 55%, ${outlineVariantFor(theme)})`;
    return css`
      outline: 0;
      box-shadow:
        0 0 0 1px ${stroke},
        0 2px 6px 0 rgba(20, 20, 50, 0.06);
    `;
  }
  const stroke = `color-mix(in oklch, ${primaryBoldFor(theme)} 70%, ${outlineVariantFor(theme)})`;
  return css`
    outline: 2px solid ${stroke};
    outline-offset: 0;
    box-shadow: ${hoverShadow(theme)};
  `;
};

// Dark intentionally reuses hover; light needs a stronger treatment
// because the hover blend is muted.
const selectedRing = ({ theme }: { theme: DefaultTheme }) => {
  if (isLightTheme(theme)) {
    return css`
      outline: 1.5px solid ${accentFor(theme)};
      outline-offset: 1px;
      box-shadow:
        0 0 0 1px ${accentStrongFor(theme)},
        0 4px 12px 0 rgba(75, 73, 218, 0.12);
    `;
  }
  return hoveredRing({ theme });
};

const dropTargetRing = ({ theme }: { theme: DefaultTheme }) => {
  if (isLightTheme(theme)) {
    return css`
      outline: 1.5px solid var(--node-card-accent);
      box-shadow:
        0 0 0 4px color-mix(in oklch, var(--node-card-accent) 45%, transparent),
        0 4px 12px rgba(75, 73, 218, 0.16);
    `;
  }
  return css`
    outline: 1.5px solid var(--node-card-accent);
    box-shadow:
      0 1px 0 rgb(255 255 255 / 0.05) inset,
      0 0 0 4px color-mix(in oklch, var(--node-card-accent) 22%, transparent),
      0 8px 26px rgba(0, 0, 0, 0.34);
  `;
};

// Output (terminal) nodes get an amber running ring instead of the
// primary one — design L12946 (.ns-node.is-terminal.is-run-running)
// overrides the lavender treatment so the "last node in the chain"
// running state stays consistent with the variant's yellow identity.
// Promoted to a theme token (`terminalRunningAmber`) so the value
// lives next to the rest of the brand-fixed state colours.
const terminalRunningAmberFor = (theme: DefaultTheme): string =>
  theme.colors?.terminalRunningAmber ?? '#eab308';

const runningRing = ({
  theme,
  $variant,
}: {
  theme: DefaultTheme;
  $variant: NodeCardVariant;
}) => {
  const isTerminal = $variant === NODE_CARD_VARIANT_OUTPUT;
  const accent = isTerminal ? terminalRunningAmberFor(theme) : primaryBoldFor(theme);
  if (isLightTheme(theme)) {
    return css`
      outline: 1.5px solid color-mix(in oklch, ${accent} 55%, transparent);
      outline-offset: -1.5px;
      box-shadow:
        0 0 0 4px color-mix(in oklch, ${accent} 12%, transparent),
        0 2px 6px 0 rgba(20, 20, 50, 0.08);
    `;
  }
  // Terminal nodes use the 75% mix per design L12946 — a touch more
  // saturated than the 55% primary mix because amber reads cooler than
  // lavender at the same alpha.
  const outlineMix = isTerminal ? '75%' : '55%';
  return css`
    outline: 2px solid color-mix(in oklch, ${accent} ${outlineMix}, transparent);
    outline-offset: -2px;
    box-shadow:
      0 1px 0 rgb(255 255 255 / 0.05) inset,
      0 0 0 6px color-mix(in oklch, ${accent} 14%, transparent),
      0 8px 26px rgba(0, 0, 0, 0.34);
  `;
};

const runDoneRing = ({ theme }: { theme: DefaultTheme }) => css`
  outline: 1.5px solid
    color-mix(in oklch, ${successFixedFor(theme)} 50%, transparent);
  outline-offset: -1.5px;
`;

const completedRing = ({ theme: { colors = {} } }: { theme: DefaultTheme }) => {
  const success = colors.success ?? '#8CD88C';
  return css`
    outline: 1px solid ${success};
  `;
};

// `runError`, not `error` — the M3 `error` is pink, tuned for body text.
const failedRing = ({ theme }: { theme: DefaultTheme }) => {
  const red = runErrorFor(theme);
  const ambient = isLightTheme(theme)
    ? '0 2px 6px 0 rgba(20, 20, 50, 0.08)'
    : '0 8px 26px rgba(0, 0, 0, 0.34)';
  return css`
    outline: 2px solid ${red};
    outline-offset: -2px;
    box-shadow:
      0 0 0 1px color-mix(in oklch, ${red} 70%, transparent),
      0 0 22px -2px color-mix(in oklch, ${red} 35%, transparent),
      ${ambient};
  `;
};

// Warning states (orphan + invalid) flag the card with an amber ring
// only — no fill or ink recolour. The card keeps its normal surface.
const orphanRing = ({ theme }: { theme: DefaultTheme }) => css`
  outline: 1.5px solid ${orphanBorderFor(theme)};
  outline-offset: -1.5px;
`;

const invalidRing = orphanRing;

// Light uses `accentStrong` instead of `primaryBold` because blending
// brand indigo with the warm `outlineVariant` lands at muddy purple.
const editModeRing = ({
  theme,
  $published,
}: {
  theme: DefaultTheme;
  $published: boolean;
}) => {
  const variant = outlineVariantFor(theme);
  const accentStroke = isLightTheme(theme)
    ? accentStrongFor(theme)
    : primaryBoldFor(theme);
  const dashed = `1.5px dashed color-mix(in oklch, ${accentStroke} 50%, ${variant})`;
  const solid = `2px solid color-mix(in oklch, ${accentStroke} 70%, ${variant})`;
  return css`
    outline: ${$published ? solid : dashed};
    background: ${editBackground(theme)};
  `;
};

const isAtRest = ($state: NodeCardState): boolean =>
  $state === NODE_CARD_STATE_DEFAULT ||
  $state === NODE_CARD_STATE_SOURCE ||
  $state === NODE_CARD_STATE_TERMINAL;

// Precedence: invalid > orphan > runDone > running > selected >
// hovered > completed/failed > default. drop-target and edit-mode
// layer separately on the root so they co-exist with run states.
const stateRing = (props: {
  $state: NodeCardState;
  $published: boolean;
  $variant: NodeCardVariant;
  theme: DefaultTheme;
}) => {
  switch (props.$state) {
    case NODE_CARD_STATE_INVALID:
      return invalidRing(props);
    case NODE_CARD_STATE_ORPHAN:
      return orphanRing(props);
    case NODE_CARD_STATE_RUNDONE:
      return runDoneRing(props);
    case NODE_CARD_STATE_RUNNING:
      return runningRing(props);
    case NODE_CARD_STATE_SELECTED:
      return selectedRing(props);
    case NODE_CARD_STATE_FAILED:
      return failedRing(props);
    case NODE_CARD_STATE_COMPLETED:
      return completedRing(props);
    case NODE_CARD_STATE_SOURCE:
      return sourceHalo(props);
    case NODE_CARD_STATE_TERMINAL:
      return terminalHalo(props);
    default:
      return restingRing(props);
  }
};

export const Root = styled.div<RootProps>`
  position: relative;
  width: ${({ $width }) => `${$width}px`};
  background: ${({ theme }) => baseBackground(theme)};
  border: 0;
  border-radius: 36px;
  display: flex;
  flex-direction: column;
  overflow: visible;
  user-select: none;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  transition:
    outline-color
      ${({ theme: { motion } }) => motion?.duration?.base ?? '160ms'} ease,
    background ${({ theme: { motion } }) => motion?.duration?.base ?? '160ms'}
      ease,
    box-shadow
      ${({ theme: { motion } }) => motion?.duration?.base ?? '160ms'} ease,
    opacity ${({ theme: { motion } }) => motion?.duration?.base ?? '160ms'}
      ease;

  ${({ $variant }) => accentVars($variant)}

  ${stateRing}

  /* Hover only flips the ring when the card is at "rest" (default,
     or the kind-resting source/terminal halos) — run / warning rings
     stay sticky so a status never silently flips while a run is live.
     Source and terminal layer the halo on top of the resting outline,
     so a hover flip cleanly replaces both with the hover ring. */
  ${({ $hovered, $emphasis, $state, theme }) =>
    ($hovered || $emphasis === NODE_CARD_EMPHASIS_HIGHLIGHTED) &&
    isAtRest($state) &&
    css`
      ${hoveredRing({ theme })}
    `}

  &:hover {
    ${({ $state, theme }) => isAtRest($state) && hoveredRing({ theme })}
  }

  ${({ $emphasis }) =>
    $emphasis === NODE_CARD_EMPHASIS_DIMMED &&
    css`
      opacity: ${NODE_CARD_DIMMED_OPACITY};

      &:hover {
        opacity: 1;
      }
    `}

  ${({ $editMode, theme, $published }) =>
    $editMode === NODE_CARD_EDIT_MODE_EDIT &&
    editModeRing({ theme, $published })}

  ${({ $dropTarget, theme }) => $dropTarget && dropTargetRing({ theme })}
`;

// Inner overlay (not the root border) so the pulse can co-exist
// with the root's outline rings.
export const PulseLayer = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 36px;
  pointer-events: none;
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.caution ?? '#FFDF9E'};
  animation: ${pulseGate} 2s infinite;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
  padding: 18px 18px 12px;
  cursor: grab;
  min-width: 0;

  &:active {
    cursor: grabbing;
  }
`;

export const CollapsedHeader = styled(Header)`
  padding: 14px 18px;
`;

export const CollapseChevron = styled.button<CollapseProps>`
  width: 24px;
  height: 24px;
  margin-right: 2px;
  flex: none;
  border: 0;
  background: transparent;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    background 120ms ease,
    color 120ms ease;

  &:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#292932'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  }

  & > svg,
  & > .material-symbols-rounded {
    display: block;
    transition: transform 160ms ease;
    width: 18px;
    height: 18px;
    font-size: 18px;
    transform: ${({ $collapsed }) =>
      $collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  }
`;

// Intentionally not per-variant — kind identity is conveyed via the
// glyph and the variant-tinted drop-target outline, not the chip.
export const HeaderIconTile = styled.div<IconTileProps>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    color 120ms ease,
    box-shadow 120ms ease;

  ${({ theme }) =>
    isLightTheme(theme)
      ? css`
          background: ${theme.colors?.surfaceContainer ?? '#F2EEE5'};
          border: 1px solid ${outlineVariantFor(theme)};
          color: ${theme.colors?.onSurface ?? '#1A1A22'};
          box-shadow: none;
        `
      : css`
          background: linear-gradient(
            180deg,
            color-mix(
              in oklch,
              ${theme.colors?.primary ?? '#C1C1FF'} 14%,
              ${theme.colors?.surfaceContainerHigh ?? '#292932'}
            ),
            color-mix(
              in oklch,
              ${theme.colors?.primary ?? '#C1C1FF'} 6%,
              ${theme.colors?.surfaceContainerHigh ?? '#292932'}
            )
          );
          border: 1px solid
            color-mix(
              in oklch,
              ${theme.colors?.primary ?? '#C1C1FF'} 18%,
              ${outlineVariantFor(theme)}
            );
          color: color-mix(
            in oklch,
            ${theme.colors?.primary ?? '#C1C1FF'} 80%,
            ${theme.colors?.onSurface ?? '#E4E1ED'}
          );
          box-shadow:
            0 1px 0 rgb(255 255 255 / 0.04) inset,
            0 2px 6px rgba(0, 0, 0, 0.55);
        `}

  & > svg,
  & > .material-symbols-rounded {
    width: 20px;
    height: 20px;
    font-size: 20px;
    display: block;
  }

  ${Root}:hover &,
  ${Root}[data-state='${NODE_CARD_STATE_SELECTED}'] &,
  ${Root}[data-hovered='true'] & {
    ${({ theme }) =>
      isLightTheme(theme)
        ? css`
            background: ${theme.colors?.primaryContainer ?? '#DBD9FF'};
            border-color: ${accentStrongFor(theme)};
            color: ${theme.colors?.onPrimaryContainer ?? '#0A006B'};
          `
        : css`
            border-color: transparent;
            box-shadow:
              0 1px 0 rgb(255 255 255 / 0.06) inset,
              0 4px 14px rgba(0, 0, 0, 0.85);
          `}
  }
`;

export const HeaderTitleStack = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

export const HeaderTitle = styled.div`
  font-family: ${({ theme: { font } }) =>
    font?.titleMedium?.fontFamily ??
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"};
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderSubtitle = styled.div`
  font-family: ${({ theme: { font } }) =>
    font?.bodySmall?.fontFamily ??
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"};
  font-size: 12.5px;
  line-height: 1.45;
  margin-top: 2px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderEnd = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level1 ?? '2px'};
  flex-shrink: 0;
  margin-left: auto;
`;

export const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;

  ${Root}:hover &,
  ${Root}[data-hovered='true'] &,
  ${Root}[data-state='${NODE_CARD_STATE_SELECTED}'] &,
  &:focus-within {
    opacity: 1;
    pointer-events: auto;
  }
`;

// Visible only when selected (not on hover) — info/delete affordances
// shouldn't steal attention until the user commits to the node.
export const FloatingActions = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transform: translateY(2px);
  pointer-events: none;
  transition:
    opacity 140ms ease,
    transform 140ms ease;
  z-index: 4;

  ${Root}[data-state='${NODE_CARD_STATE_SELECTED}'] & {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

export const CornerRunSlot = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 4;
  pointer-events: auto;
`;

// Descendant selectors recess any form atom dropped in by consumers.
// `!important` because the same atoms are styled brighter elsewhere
// (right panel, dialogs) and the recess must win in a card body.
export const Body = styled.div`
  padding: 0 18px 16px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};

  &:empty {
    padding: 0;
  }

  & input,
  & textarea,
  & select,
  & [contenteditable='true'],
  & .ns-edit__select-btn {
    background: ${({ theme }) => nodeBodyInputBgFor(theme)} !important;
  }
`;

export const Branches = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0 12px;
  border-top: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#464555'};
`;
